import React, { useRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import Loader from '../general/Loader';
import { useI18n } from '../../../i18n';
import { cx } from '../../api';
import Intent from '../../misc/ObjectActionIntent';
import { AllRoutes } from 'constants/routes';
import PagePanel from '../general/panel/PagePanel';
import Form from '../general/form/Form';
import { actions as processorActions } from '../../redux/api/processors';
import SidebarLayout from '../general/layout/SidebarLayout';
import DefaultActionBar from '../share/actionbar/DefaultActionBar';
import ActionAdd from '../share/actionbar/ActionAdd';
import Button from '../general/form/Button';
import PresenceDetectorFields from '../custom/devicePresenceDetectors/PresenceDetectorFields';
import PresenceDetectorList from '../custom/devicePresenceDetectors/PresenceDetectorList';
import PresenceDetectorView from '../custom/devicePresenceDetectors/PresenceDetectorView';
import PresenceDetectorCard from '../custom/devicePresenceDetectors/PresenceDetectorCard';
import ObjectActionDialogPopup from '../general/ObjectActionDialogPopup';
import RemoveDialog from '../general/RemoveDialog';
import { FiExternalLink as IconZoneGuards } from 'react-icons/fi';
import { setPageTitle } from '../../misc/page';
import ActionBack from '../share/actionbar/ActionBack';
import './devicePresenceDetectorsPage.scss';

const actions = processorActions.devicePresence;

const PAGE_TITLE = 'device presence detectors';

/**
 * @param {Object} props
 */

function DevicePresenceDetectorsPage(props) {
	const { f, fc } = useI18n();
	const history = useHistory();
	const form = useRef(null);
	const [intent, setIntent] = useState(null);
	const [detector, setDetector] = useState(null);
	const submitHook = useRef(null);
	const pending = useRef(false);
	const processorId = props.match.params.id;
	const modifying = props.match.params[0] == 'edit' || props.match.params[0] == 'create';
	const disabled = props.detectors.pending;

	useEffect(() => {
		setPageTitle(fc(PAGE_TITLE));
		if (props.detectors.list == null) {
			props.dispatch(actions.load.request());
		}
		return () => {
			props.detectors.error && props.dispatch(actions.clear());
		}
	}, []);

	useEffect(() => {
		props.detectors.error && props.dispatch(actions.clear());
	}, [intent]);

	useEffect(() => {
		form.current && form.current.purge();
	}, [processorId]);

	useEffect(() => {
		props.detectors.error && props.dispatch(actions.clear());
		onPopupClose();
	}, [props.match]);

	useEffect(() => {
		if (!props.detectors.pending && pending.current) {
			pending.current = false;
			if (props.detectors.error == null) {
				history.goBack();
			}
		}
	}, [props.detectors]);

	useEffect(() => {
		if (props.detectors.list != null && processorId) {
			const presenceDetector = props.detectors.map[processorId];

			presenceDetector ? setDetector(presenceDetector) : history.replace(AllRoutes.DevicePresenceDetectors);

		} else if (props.detectors.list != null && !processorId) {
			setDetector(null);
		}
		if (
			intent
			&& props.detectors.map && !props.detectors.map[intent.object().processorId]
		) {
			onPopupClose();
		}
	}, [props]);

	const submit = () => {
		submitHook.current();
	}

	const onSubmit = (presenceDetector) => {
		detector
			? props.dispatch(actions.update.request({ processorId: detector.processorId, data: presenceDetector }))
			: props.dispatch(actions.add.request({ data: presenceDetector }))
		;
		pending.current = true;
	}

	const onPopupClose = () => {
		intent && setIntent(null);
	}

	const onAdd = () => {
		history.push(`${AllRoutes.DevicePresenceDetectors}/create`);
	}

	const onBack = () => {
		setDetector(null);
		history.goBack();
	}

	const onSelect = (detector) => {
		history.push(`${AllRoutes.DevicePresenceDetectors}/${detector.processorId}`);
	}

	const onIntent = (intent) => {
		if (intent.action() == Intent.Action.Remove) {
			setIntent(intent);
		} else if (intent.action() == Intent.Action.Edit) {
			history.push(`${AllRoutes.DevicePresenceDetectors}/${intent.object().processorId}/edit`);
		}
	}

	const onRemove = (_presenceDetector) => {
		props.dispatch(actions.remove.request({ processorId: _presenceDetector.processorId }));
	}

	const sidebarContent = (<>
		<DefaultActionBar
			prependActions={modifying
				? <ActionBack onClick={onBack} />
				: <ActionAdd onClick={onAdd} />
			}
			hideEdit={modifying}
			disabled={disabled}
		/>
		{modifying
			? <div className="content">
				<PresenceDetectorFields detector={detector} onSubmit={submit} />
				<div className="error">
					{props.detectors.error}
				</div>
				<Button onClick={submit} disabled={disabled}>
					{f('save')}
				</Button>
			</div>
			: <>
				<PresenceDetectorList
					selectedId={detector ? detector.processorId : null}
					onSelect={onSelect}
					onIntent={onIntent}
				/>
				{detector && <PresenceDetectorCard detector={detector} />}
			</>
		}
	</>);

	let content = null;
	if (props.detectors.list != null) {
		content = (
			<Form
				onSubmit={onSubmit}
				submitHook={submitHook}
				objectType={cx.ods.processors.DevicePresenceDetector}
				disabled={disabled}
				customRef={form}
			>
				<SidebarLayout
					className="body"
					icon={<IconZoneGuards size="20" />}
					title={modifying ? f('device presence detector') : f('device presence detectors')}
					sidebarContent={sidebarContent}
				>
					<PresenceDetectorView
						presenceDetector={detector}
						disabled={disabled}
						editMode={modifying}
					/>
					{intent && intent.action() == Intent.Action.Remove && (
						<ObjectActionDialogPopup
							offset={intent.data().offset}
							onClose={onPopupClose}
							title={f('delete presence detector')}
							disabled={disabled}
							error={props.detectors.error}
						>
							<RemoveDialog
								object={intent.object()}
								title={f('presence detector')}
								text={f('are you sure you want to delete device presence detector', { name: intent.object().name })}
								onSubmit={onRemove}
								onCancel={onPopupClose}
							/>
						</ObjectActionDialogPopup>
					)}
				</SidebarLayout>
			</Form>
		);
	} else if (props.detectors.pending) {
		content = <div className="vh-center"><Loader size={Loader.Size.LG} /></div>
	}

	return (
		<div className='device-presence-detectors app-page'>
			<PagePanel>
				{content}
			</PagePanel>
		</div>
	);
}

DevicePresenceDetectorsPage.Url = AllRoutes.DevicePresenceDetectors;

export default connect(
	state => ({
		detectors: state.processors.devicePresence
	})
)(DevicePresenceDetectorsPage);
